import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Users, Wallet, ShieldCheck, Check, Edit, RotateCcw, Search, AlertTriangle, ChevronLeft } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Av, Bg, Bt, Cd, Sec, KpiStrip, Tbl } from "../components";
import { notify, confirmDialog } from "../notify";

const MTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtP = (p) => { if (!p) return "—"; const [y, m] = p.split("-"); return MTHS[parseInt(m, 10)] + " " + y; };
const inr = (v) => "₹" + Math.round(v || 0).toLocaleString("en-IN");
const dmy = (d) => { if (!d) return ""; const x = new Date(d); return `${String(x.getDate()).padStart(2, "0")}/${String(x.getMonth() + 1).padStart(2, "0")}/${x.getFullYear()}`; };

const LIFE_CLR = { "Active": K.g, "On Notice Period": K.a, "Resignation Submitted": K.a, "Exit In Progress": K.a, "FnF Processing": K.a, "Inactive": K.r };
const LifeTag = ({ s }) => s && s !== "Active" ? <span className="inline-block px-[6px] py-[1px] rounded-full text-[11px] font-semibold whitespace-nowrap" style={{ color: LIFE_CLR[s] || K.t3, background: (LIFE_CLR[s] || K.t3) + "1f" }}>{s}</span> : null;

// Last six months, newest first — the portal normally reviews the previous month.
const periodChoices = () => {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 1; i <= 6; i++) { const x = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); }
  return out;
};

const PERIOD_SEL = "px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0";

// Read-only salary lines for one person.
const figureCols = (extra = [], onOpen) => [
  { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[8px] min-w-[170px]"><Av nm={r.name} sz={28} /><div><div className={`font-bold text-t0 text-[14px] ${onOpen ? "cursor-pointer hover:underline" : ""}`} title={onOpen ? "Click to see the full salary breakdown" : undefined} onClick={onOpen ? () => onOpen(r) : undefined}>{r.name}</div><div className="text-[11px] text-t3">{r.employee_id}{r.department ? ` · ${r.department}` : ""}</div><LifeTag s={r.lifecycle} /></div></div> },
  { k: "days", l: "Days", r: r => r.figures ? <span className="text-[13px] text-t1">{r.figures.presentDays}/{r.figures.daysInMonth}{r.figures.lopDays > 0 && <span className="text-r"> · LOP {r.figures.lopDays}</span>}</span> : <span className="text-t3">—</span> },
  { k: "gross", l: "Earned Gross", r: r => r.figures ? <span className="font-semibold text-[13px]">{inr(r.figures.earned)}</span> : <span className="text-t3 text-[12px]">Not available</span> },
  { k: "arrear", l: "Arrear", r: r => r.figures ? <span className="text-[13px] text-g">{inr(r.figures.arrear)}</span> : "—" },
  { k: "oth", l: "Reimb. + BYOD", r: r => r.figures ? <span className="text-[13px]">{inr(r.figures.reimbursement + r.figures.byod)}</span> : "—" },
  { k: "ghi", l: "GHI", r: r => r.figures ? <span className="text-[13px] text-r">{inr(r.figures.ghi)}</span> : "—" },
  { k: "ded", l: "Other Deductions", r: r => r.figures ? <span className="text-[13px] text-r" title={`PF ${inr(r.figures.pf)} · TDS ${inr(r.figures.tds)} · Prof. Tax ${inr(r.figures.pt || 0)} · ESI ${inr(r.figures.esi || 0)} · Work ${inr(r.figures.work_deduction)} · Loan ${inr(r.figures.loan_emi)}`}>{inr(r.figures.pf + r.figures.tds + (r.figures.pt || 0) + (r.figures.esi || 0) + r.figures.work_deduction + r.figures.loan_emi)}</span> : "—" },
  { k: "tot", l: "Total Deductions", r: r => r.figures ? <span className="text-[13px] font-semibold text-r">{inr(r.figures.totalDed)}</span> : "—" },
  { k: "net", l: "Net Pay", r: r => r.figures ? <span className="font-bold text-g text-[14px]">{inr(r.figures.net)}</span> : "—" },
  ...extra,
];

export const TeamSalaryPage = ({ role }) => {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(null);

  useEffect(() => {
    api.get("/api/team-salary/meta").then(d => {
      const m = d.data || d; setMeta(m);
      setTab(t => t || (m.canManager && m.teamSize > 0 ? "team" : m.canHr ? "hr" : m.canManager ? "team" : null));
    }).catch(() => setMeta({}));
  }, []);

  if (!meta) return <div className="p-[30px] text-t3 text-[14px]">Loading…</div>;
  const both = meta.canManager && meta.canHr;
  const tabs = [meta.canManager && { id: "team", l: both ? "My team" : "My Team's Salary", ic: Users }, meta.canHr && { id: "hr", l: both ? "All teams" : "HR Review & Approval", ic: ShieldCheck }].filter(Boolean);
  if (!tabs.length) return <Cd><div className="text-center p-[30px] text-t3 text-[14px]">You do not have access to Team Salary. Ask an admin to enable it in Settings → Permissions.</div></Cd>;

  return <div>
    <div className="mb-3">
      <div className="text-[22px] font-extrabold text-t0">Team Salary</div>
      <div className="text-[14px] text-t2">Verify last month's salary of your team before payroll is processed.</div>
    </div>
    {tabs.length > 1 && <div className="mb-3">
      <div className="text-[12px] text-t3 mb-[2px]">Show</div>
      <select value={tab || ""} onChange={e => setTab(e.target.value)} className={PERIOD_SEL}>
        {tabs.map(t => <option key={t.id} value={t.id}>{t.l}</option>)}
      </select>
    </div>}
    {tab === "team" && <ManagerView meta={meta} />}
    {tab === "hr" && <HrView meta={meta} />}
  </div>;
};

// ── Full breakdown for one person: earnings, deductions, loss-of-pay days with dates and reasons ──
const KIND_CLR = { ok: K.g, part: K.pu, leave: K.cy, lop: K.r, info: K.t3 };
const DetailModal = ({ row, period, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [onlyLop, setOnlyLop] = useState(false);
  useEffect(() => {
    api.get(`/api/team-salary/detail?period=${period}&employee_id=${encodeURIComponent(row.employee_id)}`)
      .then(r => setD(r.data || r)).catch(e => setErr(e.message || "Could not load"));
  }, [row.employee_id, period]);
  const Line = ({ l, v, hint, c, bold }) => <div className="flex items-start justify-between gap-3 py-[5px] border-b border-b0 last:border-0">
    <div className="min-w-0"><div className={`text-[14px] ${bold ? "font-bold" : ""} text-t0`}>{l}</div>{hint && <div className="text-[12px] text-t3 break-words">{hint}</div>}</div>
    <div className={`text-[14px] font-mono whitespace-nowrap ${bold ? "font-bold" : ""}`} style={{ color: c }}>{v}</div>
  </div>;
  const s = d?.summary;
  const days = d ? (onlyLop ? d.days.filter(x => x.lop > 0) : d.days) : [];
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[860px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div><div className="text-[18px] font-extrabold text-t0">{row.name}</div>
          <div className="text-[13px] text-t3">{row.employee_id}{row.department ? ` · ${row.department}` : ""} · Salary for {fmtP(period)}</div></div>
        <Bt v="secondary" s="sm" onClick={onClose}>Close</Bt>
      </div>
      {err && <div className="text-r text-[14px]">{err}</div>}
      {!d && !err && <div className="p-[24px] text-center text-t3 text-[14px]">Loading…</div>}
      {d && !s && <div className="p-[24px] text-center text-t3 text-[14px]">Salary could not be calculated for this person — their CTC or salary structure is not filled in yet.</div>}
      {d && s && <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {[["Working days", d.workingDays], ["Days paid (present)", s.presentDays], ["Paid leave", s.paidLeaveDays], ["Loss of pay (LOP)", s.lopDays]].map(([l, v], i) =>
            <div key={l} className="rounded-xl border border-b0 bg-bg2 px-3 py-2"><div className="text-[11px] uppercase tracking-wide text-t3 font-bold">{l}</div>
              <div className="text-[20px] font-extrabold" style={{ color: i === 3 && v > 0 ? K.r : undefined }}>{v}</div></div>)}
        </div>
        {s.prorated && <div className="mb-3 text-[13px] text-t2">Joined during this month — pay is prorated from the joining date.</div>}
        {s.fromSheet && <div className="mb-3 text-[13px] text-t2">This month's figures come from an uploaded salary sheet, not from attendance.</div>}

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="rounded-xl border border-b0 p-3">
            <div className="text-[14px] font-bold text-t0 mb-1">Earnings</div>
            {d.earnings.map(x => <Line key={x.label} l={x.label} v={inr(x.amount)} hint={x.hint} />)}
            {d.lopLines.length > 0 && <>
              <div className="text-[12px] text-t3 mt-2">Full month would have been {inr(d.fullGross)}. Cut for loss of pay:</div>
              {d.lopLines.map(x => <Line key={x.label} l={`− ${x.label}`} v={`− ${inr(x.amount)}`} hint={x.hint} c={K.r} />)}</>}
            <Line l="Earned gross" v={inr(d.gross)} bold />
            {d.reimbursement > 0 && <Line l="Reimbursements + BYOD" v={inr(d.reimbursement)} />}
          </div>
          <div className="rounded-xl border border-b0 p-3">
            <div className="text-[14px] font-bold text-t0 mb-1">Deductions</div>
            {d.deductions.length === 0 && <div className="text-[13px] text-t3 py-1">No deductions this month.</div>}
            {d.deductions.map(x => <Line key={x.label} l={x.label} v={inr(x.amount)} hint={x.hint} c={K.r} />)}
            <Line l="Total deductions" v={inr(d.totalDed)} bold c={K.r} />
            <div className="mt-2 pt-2 border-t border-b0"><Line l="Net pay" v={inr(d.net)} bold c={K.g} /></div>
          </div>
        </div>

        {s.lopDays > 0 && <div className="rounded-xl border border-b0 p-3 mb-4" style={{ background: "rgba(239,68,68,.05)" }}>
          <div className="text-[14px] font-bold text-t0 mb-1">Why {s.lopDays} day{s.lopDays === 1 ? "" : "s"} of loss of pay</div>
          <div className="text-[13px] text-t2">{s.lopLeaveWithoutPay > 0 && <>{s.lopLeaveWithoutPay} day(s) on approved leave without pay. </>}{s.lopAbsent > 0 && <>{s.lopAbsent} day(s) absent with no check-in and no approved leave. </>}{s.lopFlex > 0 && <>{s.lopFlex} day(s) for short flexible-shift hours. </>}See the days marked in red below.</div>
        </div>}

        <div className="flex items-center justify-between mb-1">
          <div className="text-[14px] font-bold text-t0">Day by day</div>
          <label className="text-[13px] text-t2 flex items-center gap-1"><input type="checkbox" checked={onlyLop} onChange={e => setOnlyLop(e.target.checked)} /> Show only loss-of-pay days</label>
        </div>
        <div className="rounded-xl border border-b0 overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="bg-bg2 text-t3 text-left"><th className="px-2 py-1">Date</th><th className="px-2 py-1">Status</th><th className="px-2 py-1">Details / reason</th><th className="px-2 py-1 text-right">LOP</th></tr></thead>
            <tbody>{days.map(x => <tr key={x.date} className="border-t border-b0" style={x.lop > 0 ? { background: "rgba(239,68,68,.06)" } : undefined}>
              <td className="px-2 py-1 whitespace-nowrap text-t1">{dmy(x.date)} <span className="text-t3">{x.day}</span></td>
              <td className="px-2 py-1 font-semibold whitespace-nowrap" style={{ color: KIND_CLR[x.kind] }}>{x.status}</td>
              <td className="px-2 py-1 text-t2">{x.note}</td>
              <td className="px-2 py-1 text-right font-mono" style={{ color: x.lop > 0 ? K.r : undefined }}>{x.lop > 0 ? x.lop : "—"}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {d.history?.length > 0 && <div className="mt-5">
          <div className="text-[14px] font-bold text-t0 mb-1">Month-wise record — manager's decision and reason</div>
          <div className="rounded-xl border border-b0 overflow-hidden">
            <table className="w-full border-collapse text-[13px]">
              <thead><tr className="bg-bg2 text-t3 text-left"><th className="px-2 py-1">Month</th><th className="px-2 py-1">Manager's decision</th><th className="px-2 py-1">Reason / remark</th><th className="px-2 py-1">Payroll approval</th></tr></thead>
              <tbody>{d.history.map(h => <tr key={h.period} className="border-t border-b0 align-top">
                <td className="px-2 py-1 whitespace-nowrap font-semibold text-t1">{fmtP(h.period)}</td>
                <td className="px-2 py-1 whitespace-nowrap" style={{ color: h.decision === "approved" ? K.g : h.decision ? K.a : K.t3 }}>{h.decision ? DEC_LABEL[h.decision] : "—"}{h.manager_name && <div className="text-[11px] text-t3">{h.manager_name} · {dmy(h.decided_at)}</div>}</td>
                <td className="px-2 py-1 text-t2 whitespace-pre-wrap break-words">{h.remark || "—"}</td>
                <td className="px-2 py-1 whitespace-nowrap">{h.approved_at ? <><span style={{ color: K.g }}>Approved</span><div className="text-[11px] text-t3">{h.approved_by_name} · {dmy(h.approved_at)}</div>{h.hr_note && <div className="text-[11px] text-t2">{h.hr_note}</div>}</> : <span className="text-t3">—</span>}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </div>}
        {d.lopFromDays !== s.lopDays && <div className="text-[12px] text-t3 mt-2">The day list adds up to {d.lopFromDays} LOP day(s); payroll's total is {s.lopDays}. Payroll's total is the one used for pay.</div>}
      </>}
    </div>
  </div>;
};

// ── Manager: team figures; approve or ask for a change per person (or for a selection), then submit ──
const DEC_LABEL = { approved: "Approved", changes: "Changes needed" };
const DecisionTag = ({ dec }) => dec
  ? <div className="max-w-[260px]"><Bg c={dec.decision === "approved" ? K.g : K.a} sm dot>{DEC_LABEL[dec.decision]}</Bg>
      {dec.remark && <div className="text-[12px] text-t2 mt-[3px] whitespace-pre-wrap break-words">{dec.remark}</div>}</div>
  : <span className="text-[12px] text-t3">Not decided</span>;

// One remark for one person, or the same remark for everyone selected.
const DecisionModal = ({ names, decision, onClose, onSave }) => {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const need = decision === "changes";
  const go = async () => {
    if (need && !remark.trim()) return notify("Write what needs to change.");
    setBusy(true); try { await onSave(remark.trim()); } finally { setBusy(false); }
  };
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[520px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
      <div className="text-[18px] font-extrabold text-t0">{need ? "Changes needed" : "Approve"} — {names.length === 1 ? names[0] : `${names.length} people`}</div>
      {names.length > 1 && <div className="text-[13px] text-t2 mt-1">{names.slice(0, 8).join(", ")}{names.length > 8 ? ` and ${names.length - 8} more` : ""}. The same remark is saved for each of them.</div>}
      <textarea autoFocus value={remark} onChange={e => setRemark(e.target.value)} rows={5}
        placeholder={need ? "What needs to change, and why? e.g. add arrear ₹2,000 (project bonus) / do not mark LOP on 5–7 Oct (approved WFH)" : "Remark (optional)"}
        className="w-full mt-3 bg-bg2 border border-b0 rounded-[10px] p-3 text-[14px] text-t0 outline-none resize-y leading-relaxed" />
      <div className="flex gap-2 mt-4"><Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt>
        <Bt v={need ? "warning" : "success"} full onClick={go} disabled={busy}>{busy ? "Saving…" : need ? "Save — changes needed" : "Save — approved"}</Bt></div>
    </div>
  </div>;
};

const ManagerView = ({ meta }) => {
  const [period, setPeriod] = useState(meta.window.period);
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [dlg, setDlg] = useState(null);   // { ids, decision }
  const load = useCallback(() => {
    api.get(`/api/team-salary/mine?period=${period}`).then(r => { const x = r.data || r; setD(x); }).catch(e => notify(e.message || "Could not load", { title: "Error", variant: "error" }));
  }, [period]);
  useEffect(() => { setD(null); setPicked(new Set()); load(); }, [load]);

  const win = meta.window;
  const open = win.open || meta.isAdmin;
  const canAct = meta.canSubmit && open && (period === win.period || meta.isAdmin);
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const nameOf = (id) => d.rows.find(r => r.employee_id === id)?.name || id;

  const saveDecision = async (remark) => {
    try {
      await api.post("/api/team-salary/decisions", { period, employee_ids: dlg.ids, decision: dlg.decision, remark });
      notify(`${dlg.ids.length === 1 ? nameOf(dlg.ids[0]) : dlg.ids.length + " people"} marked ${DEC_LABEL[dlg.decision].toLowerCase()}`, { title: "Saved", variant: "success" });
      setDlg(null); setPicked(new Set()); load();
    } catch (e) { notify(e.message || "Could not save", { title: "Error", variant: "error" }); }
  };
  const submit = async () => {
    const changes = d.rows.filter(r => r.decision?.decision === "changes").length;
    const msg = changes ? `Send your review of ${fmtP(period)} to Centre HR? ${changes} ${changes === 1 ? "person has" : "people have"} a change request.`
      : `Send your review of ${fmtP(period)} to Centre HR with everyone approved? If you do not approve, your team's salary will not be processed and you will be responsible for that.`;
    if (!(await confirmDialog(msg))) return;
    setBusy(true);
    try { await api.post("/api/team-salary/review", { period, remark: note }); notify("Submitted to Centre HR / HR Head", { title: "Saved", variant: "success" }); load(); }
    catch (e) { notify(e.message || "Could not submit", { title: "Error", variant: "error" }); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="p-[30px] text-t3 text-[14px]">Loading team…</div>;
  const rv = d.review;
  const decided = d.rows.filter(r => r.decision).length;
  const allDecided = d.rows.length > 0 && decided === d.rows.length;
  const cols = [
    ...(canAct ? [{ k: "sel", l: "", r: r => <input type="checkbox" checked={picked.has(r.employee_id)} onChange={() => toggle(r.employee_id)} /> }] : []),
    ...figureCols([
      { k: "dec", l: "Your decision", r: r => <DecisionTag dec={r.decision} /> },
      ...(canAct ? [{ k: "act", l: "", r: r => <div className="flex gap-1">
        <Bt v="success" s="xs" icon={Check} onClick={() => setDlg({ ids: [r.employee_id], decision: "approved" })}>Approve</Bt>
        <Bt v="warning" s="xs" onClick={() => setDlg({ ids: [r.employee_id], decision: "changes" })}>Change</Bt>
      </div> }] : []),
    ], setDetail),
  ];
  return <div>
    <div className="flex flex-wrap items-center gap-3 mb-3">
      <div><div className="text-[12px] text-t3 mb-[2px]">Salary month</div>
        <select value={period} onChange={e => setPeriod(e.target.value)} className={PERIOD_SEL}>
          {[...new Set([win.period, ...periodChoices()])].map(p => <option key={p} value={p}>{fmtP(p)}</option>)}
        </select></div>
      <div className="px-3 py-2 rounded-lg text-[14px] flex-1 min-w-[240px]" style={{ background: open ? "rgba(16,185,129,.10)" : "rgba(245,158,11,.10)", border: `1px solid ${open ? "rgba(16,185,129,.30)" : "rgba(245,158,11,.35)"}` }}>
        {meta.isAdmin ? <>The portal is <b>always open</b> for Admin.</> : win.open ? <>The portal is <b>open</b> — you can verify your team's salary until the <b>{win.to}rd</b> of this month.</>
          : <>The portal is <b>closed</b>. It opens from the <b>{win.from}st to the {win.to}rd</b> of every month. You can still view the figures.</>}
      </div>
    </div>

    <KpiStrip items={[
      { icon: Users, label: "Team members", value: d.rows.length, c: K.bl },
      { icon: Wallet, label: "Gross (incl. arrear)", value: inr(d.totals.gross), c: K.g },
      { icon: Wallet, label: "Arrears", value: inr(d.totals.arrear), c: K.p },
      { icon: Wallet, label: "Total deductions", value: inr(d.totals.totalDed), c: K.r },
      { icon: Wallet, label: "Net payable", value: inr(d.totals.net), c: K.g },
    ]} />

    {canAct && picked.size > 0 && <div className="sticky top-0 z-10 mb-3 px-3 py-2 rounded-lg bg-bg1 border border-b1 flex flex-wrap items-center gap-3">
      <span className="text-[14px] text-t0 font-semibold">{picked.size} selected</span>
      <Bt v="success" icon={Check} s="sm" onClick={() => setDlg({ ids: [...picked], decision: "approved" })}>Approve selected</Bt>
      <Bt v="warning" s="sm" onClick={() => setDlg({ ids: [...picked], decision: "changes" })}>Changes needed for selected</Bt>
      <Bt v="ghost" s="sm" onClick={() => setPicked(new Set())}>Clear</Bt>
    </div>}

    <Cd style={{ marginBottom: 12 }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Sec icon={Users} title={`${fmtP(period)} — your team`} />
        {canAct && d.rows.length > 1 && <div className="flex gap-2">
          <Bt v="ghost" s="xs" onClick={() => setPicked(new Set(d.rows.map(r => r.employee_id)))}>Select all</Bt>
          <Bt v="ghost" s="xs" onClick={() => setPicked(new Set(d.rows.filter(r => !r.decision).map(r => r.employee_id)))}>Select undecided</Bt>
        </div>}
      </div>
      <div className="text-[12px] text-t3 mb-2">View only — figures cannot be edited here. For each person choose <b>Approve</b> or <b>Change</b> and write the reason. To give several people the same remark, tick them and use the bar above; untick anyone who needs a different remark and set theirs on their own row. Click a name to see the full breakdown.</div>
      {d.rows.some(r => r.figures?.source === 'system') && <div className="text-[12px] text-t3 mb-2">Salary is calculated by the system from each person's attendance, leave and salary structure for {fmtP(period)}.</div>}
      {d.rows.length ? <Tbl cols={cols} data={d.rows} /> : <div className="text-center p-[24px] text-t3 text-[14px]">No one reports to you.</div>}
    </Cd>

    {d.rows.length > 0 && <Cd>
      <Sec icon={ShieldCheck} title="Send to Centre HR" />
      <div className="text-[14px] text-t1 mb-2"><b>{decided} of {d.rows.length}</b> decided{!allDecided && canAct ? " — decide for everyone before sending." : "."}</div>
      {rv && <div className="mb-3 px-3 py-2 rounded-lg text-[14px] bg-gb border border-gd text-g">
        Submitted on {dmy(rv.submitted_at)} — <b>{rv.has_changes ? "changes requested" : "approved"}</b>. Centre HR has been notified. If you change a decision afterwards, send it again.
      </div>}
      {!rv && canAct && <div className="mb-3 px-3 py-2 rounded-lg text-[14px] bg-ab border border-ad text-a flex gap-2"><AlertTriangle size={16} className="flex-shrink-0 mt-[2px]" />
        Check the figures above. If you do not approve, your team's salary will not be processed and you will be responsible for that.</div>}
      {canAct && <>
        <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Optional overall note for Centre HR" rows={2}
          className="w-full bg-bg2 border border-b0 rounded-[10px] p-3 text-[14px] text-t0 outline-none resize-y" />
        <div className="flex flex-wrap gap-2 mt-3">
          <Bt v="success" icon={Check} disabled={busy || !allDecided} onClick={submit}>{rv ? "Send again to Centre HR" : "Send to Centre HR"}</Bt>
        </div></>}
      {!canAct && <div className="text-[12px] text-t3 mt-2">{!meta.canSubmit ? "You can view this page but not submit a review." : period !== win.period ? `Only ${fmtP(win.period)} can be reviewed right now.` : "Submitting is possible only while the portal is open."}</div>}
    </Cd>}
    {dlg && <DecisionModal names={dlg.ids.map(nameOf)} decision={dlg.decision} onClose={() => setDlg(null)} onSave={saveDecision} />}
    {detail && <DetailModal row={detail} period={period} onClose={() => setDetail(null)} />}
  </div>;
};

// ── Centre HR / HR Head: per-manager remark, edit arrears & deductions, approve ──
const EditModal = ({ row, labels, period, onClose, onSaved }) => {
  const keys = Object.keys(labels);
  const [vals, setVals] = useState(() => Object.fromEntries(keys.map(k => [k, String(row.figures?.[k] ?? 0)])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const fields = {}; keys.forEach(k => { if (Number(vals[k]) !== Number(row.figures?.[k] ?? 0)) fields[k] = Number(vals[k]); });
      if (!Object.keys(fields).length) { onClose(); return; }
      await api.put("/api/team-salary/hr/figures", { period, employee_id: row.employee_id, fields });
      notify("Figures updated", { title: "Saved", variant: "success" }); onSaved();
    } catch (e) { notify(e.message || "Could not save", { title: "Error", variant: "error" }); setBusy(false); }
  };
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[460px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
      <div className="text-[18px] font-extrabold text-t0">Edit arrears & deductions</div>
      <div className="text-[14px] text-t2 mb-3">{row.name} · {fmtP(period)}</div>
      <div className="grid grid-cols-2 gap-3">
        {keys.map(k => <label key={k} className="block"><span className="text-[12px] text-t3">{labels[k]} (₹)</span>
          <input type="number" min="0" value={vals[k]} onChange={e => setVals(v => ({ ...v, [k]: e.target.value }))} className="w-full mt-[2px] py-[7px] px-[10px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" /></label>)}
      </div>
      <div className="text-[12px] text-t3 mt-3">Every change is logged. Net pay is recalculated from these lines.</div>
      <div className="flex gap-2 mt-4"><Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt><Bt full onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Bt></div>
    </div>
  </div>;
};

const HrView = ({ meta }) => {
  const [period, setPeriod] = useState(meta.window.period);
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [picked, setPicked] = useState(() => new Set());
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/api/team-salary/hr?period=${period}`).then(r => setD(r.data || r)).catch(e => notify(e.message || "Could not load", { title: "Error", variant: "error" }));
  }, [period]);
  useEffect(() => { setD(null); setPicked(new Set()); load(); }, [load]);

  const groups = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    const m = new Map();
    for (const r of d.rows) {
      if (needle && !(`${r.name} ${r.employee_id} ${r.manager_name || ""}`).toLowerCase().includes(needle)) continue;
      if (filter === "pending" && r.approved) continue;
      if (filter === "approved" && !r.approved) continue;
      const k = r.manager_id || "_none";
      if (!m.has(k)) m.set(k, { id: k, name: r.manager_name || "No reporting manager", rows: [] });
      m.get(k).rows.push(r);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [d, q, filter]);

  const approve = async (ids, force = false) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      await api.post("/api/team-salary/hr/approve", { period, employee_ids: ids, force });
      notify(`${ids.length} approved for payroll`, { title: "Saved", variant: "success" }); setPicked(new Set()); load();
    } catch (e) {
      // The manager has not signed off yet: ask once; saying No simply leaves things as they are (not an error).
      if (/not submitted/i.test(e.message || "")) {
        const go = await confirmDialog("Their manager has not submitted the team salary review yet. Approve for payroll anyway?", { title: "Manager has not submitted", okLabel: "Approve anyway", cancelLabel: "Not now" });
        if (go) { setBusy(false); return approve(ids, true); }
        return;
      }
      notify(e.message || "Could not approve", { title: "Error", variant: "error" });
    } finally { setBusy(false); }
  };
  const revoke = async (r) => {
    if (!(await confirmDialog(`Remove payroll approval for ${r.name}?`))) return;
    try { await api.del(`/api/team-salary/hr/approve/${period}/${encodeURIComponent(r.employee_id)}`); load(); } catch (e) { notify(e.message || "Failed", { variant: "error" }); }
  };
  const reopen = async (rv) => {
    if (!(await confirmDialog("Delete this review so the manager can submit again?"))) return;
    try { await api.del(`/api/team-salary/review/${rv.id}`); load(); } catch (e) { notify(e.message || "Failed", { variant: "error" }); }
  };
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!d) return <div className="p-[30px] text-t3 text-[14px]">Loading…</div>;
  const total = d.rows.length, done = d.rows.filter(r => r.approved).length;
  const submitted = Object.keys(d.reviews).length;

  return <div>
    <div className="flex flex-wrap items-end gap-3 mb-3">
      <div><div className="text-[12px] text-t3 mb-[2px]">Salary month</div>
        <select value={period} onChange={e => setPeriod(e.target.value)} className={PERIOD_SEL}>
          {[...new Set([meta.window.period, ...periodChoices()])].map(p => <option key={p} value={p}>{fmtP(p)}</option>)}
        </select></div>
      <div className="relative flex-1 min-w-[200px]"><Search size={13} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-t3" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search employee or manager…" className="w-full py-[7px] pl-[32px] pr-[10px] bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none" /></div>
      <select value={filter} onChange={e => setFilter(e.target.value)} className={PERIOD_SEL}>
        <option value="all">Everyone</option><option value="pending">Awaiting approval</option><option value="approved">Approved</option>
      </select>
    </div>
    <KpiStrip items={[
      { icon: Users, label: "Employees", value: total, c: K.bl },
      { icon: ShieldCheck, label: "Approved for payroll", value: `${done} / ${total}`, c: K.g },
      { icon: Check, label: "Managers submitted", value: submitted, c: K.p },
    ]} />
    {meta.canApprove && picked.size > 0 && <div className="sticky top-0 z-10 mb-3 px-3 py-2 rounded-lg bg-bg1 border border-b1 flex items-center gap-3">
      <span className="text-[14px] text-t0 font-semibold">{picked.size} selected</span>
      <Bt v="success" icon={Check} s="sm" disabled={busy} onClick={() => approve([...picked])}>Approve selected</Bt>
      <Bt v="ghost" s="sm" onClick={() => setPicked(new Set())}>Clear</Bt>
    </div>}

    {groups.length === 0 && <Cd><div className="text-center p-[24px] text-t3 text-[14px]">Nobody matches.</div></Cd>}
    {groups.map(g => {
      const rv = d.reviews[g.id];
      const pend = g.rows.filter(r => !r.approved);
      return <Cd key={g.id} style={{ marginBottom: 12 }}>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[16px] font-extrabold text-t0">{g.name}</span>
          <span className="text-[12px] text-t3">{g.rows.length} member{g.rows.length === 1 ? "" : "s"}</span>
          {g.id !== "_none" && (rv ? <Bg c={rv.has_changes ? K.a : K.g} sm dot>{rv.has_changes ? "Changes requested" : "Approved by manager"} · {dmy(rv.submitted_at)}</Bg> : <Bg c={K.t3} sm dot>Manager has not submitted</Bg>)}
          <div className="flex-1" />
          {rv && meta.canReopen && <Bt v="ghost" s="xs" icon={RotateCcw} onClick={() => reopen(rv)}>Reopen review</Bt>}
          {meta.canApprove && pend.length > 0 && <Bt v="success" s="sm" icon={Check} disabled={busy} onClick={() => approve(pend.map(r => r.employee_id))}>Approve all ({pend.length})</Bt>}
        </div>
        {rv?.remark && <div className="mb-3 px-3 py-2 rounded-lg bg-bg2 border border-b0">
          <div className="text-[11px] uppercase tracking-wider text-t3 font-bold mb-1">Manager's remark</div>
          <div className="text-[14px] text-t0 whitespace-pre-wrap leading-relaxed">{rv.remark}</div>
        </div>}
        <Tbl cols={[
          ...(meta.canApprove ? [{ k: "sel", l: "", r: r => r.approved ? null : <input type="checkbox" checked={picked.has(r.employee_id)} onChange={() => toggle(r.employee_id)} /> }] : []),
          ...figureCols([
            { k: "dec", l: "Manager's decision", r: r => <DecisionTag dec={r.decision} /> },
            { k: "st", l: "Payroll approval", r: r => r.approved ? <Bg c={K.g} sm dot>Approved{r.approved_by_name ? ` · ${r.approved_by_name}` : ""}</Bg> : <Bg c={K.a} sm dot>Awaiting</Bg> },
            { k: "act", l: "", r: r => <div className="flex gap-1">
              {meta.canEditFigures && r.figures?.editable && !r.approved && <Bt v="secondary" s="xs" icon={Edit} onClick={() => setEdit(r)}>Edit</Bt>}
              {meta.canReopen && r.approved && <Bt v="ghost" s="xs" icon={RotateCcw} onClick={() => revoke(r)}>Undo</Bt>}
            </div> },
          ], setDetail),
        ]} data={g.rows} />
      </Cd>;
    })}
    {detail && <DetailModal row={detail} period={period} onClose={() => setDetail(null)} />}
    {edit && <EditModal row={edit} labels={d.editableFields} period={period} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
  </div>;
};
